<template>
  <v-container>
    <div class="elevation-3 ml-3 mr-3 ma-0 pl-3 pr-3 pt-3 pb-2">
      <v-row dense>
        <v-spacer></v-spacer>
        <v-col cols="2" class="pt-0">
          <v-btn color="primary" rounded dense small @click="printStatusCodesReport">
            Print
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <template>
      <CRUDList
        ref="crudList"
        :canAdd="canAddToSharedList"
        :canEdit="true"
        :canSave="canEditSharedList"
        :showSelect="false"
        :enableFixedHeaders="true"
        :displayTopPagination="false"
        :headers="headers"
        :selectedItems="selectedItems"
        :baseCrudKey="baseCrudKey"
        :dialogHeaderName="dialogHeaderName"
        :defaultSortBy="defaultSortBy"
        :tableTitle="dialogHeaderName"
        :dataMapper="dataMapper"
        :variableToChangeRowBgColor="variableToChangeRowBgColor"
        dialogDynamicWidth="30vw"
        tableWrapperDynamicClassname='elevation-3 mt-4 ma-3 pt-0 pa-1 taxroll-table-wrapper'
        tableDynamicClassname='taxroll-custom-table'
        tableDynamicHeight='400'
        @emitSelectedItem="emitSelectedItem"
        @loadCRUDList="getTaxStatusCodesSharedRecords"
        @upsertItem="upsertRecords"
        @deleteItem="deleteFromDiaglog"
        :canDeleteInDialog="canDeleteFromSharedList"
        :loadingData="gridLoading.flag"
        :loadingText="gridLoading.text"
        >
        <template v-slot:[`editedItem`]="{ }">
          <taxStatusCodesDialog />
        </template>
      </CRUDList>
    </template>
  </v-container>
</template>

<script>
import {
  mapMutations, mapActions, mapState, mapGetters,
} from 'vuex';
import CRUDList from '../../common/base/crud-2.0/CRUDList.vue';
import { taxStatusCodesHeader } from '../Utils/taxHeaders.utils';
import taxStatusCodesDialog from './taxStatusCodesDialog.vue';
import { defaultGridLoading, manageGridLoading } from '../../../util/shared/vue-global';
import { isAuthorised } from '../../../store/auth/token';
import { authRoles } from '../../../router/roleGroups';
import { generateQueryString } from '../../common/base/report/report-util';
import { clients } from '../../../util/clients';
import SharedListAccessControlMixin from '../SharedListAccessControl.mixin';
import { reservedStatusCode } from '../../../util/shared/tmc-global';
import {
  getHighlightColorForStatusCode, getStatusCodeRowColorMap, applyStatusCodeHighlight,
} from '../../tax/common-utils/tax-common.util';
import '../../../assets/style.css';

const { backendRest } = clients.direct;
export default {
  name: 'taxStatusCodesList',
  mixins: [SharedListAccessControlMixin],
  components: {
    CRUDList,
    taxStatusCodesDialog,
  },
  data() {
    return {
      selectedRows: [],
      baseCrudKey: 'taxStatusCodesList',
      selectedRow: null,
      dialogHeaderName: 'Status Codes',
      headers: taxStatusCodesHeader,
      gridLoading: defaultGridLoading(),
    };
  },
  computed: {
    ...mapState({
      StoreSelItems: (state) => state.shared.taxStatusCodesShared.selectedItems,
    }),
    ...mapGetters('base/crud', [
      'criteria', 'item',
    ]),
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    selectedItems: {
      get() {
        return this.StoreSelItems;
      },
      set(value) {
        this.setSelectedItems(value);
      },
    },
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    defaultSortBy() {
      return [
        {
          column: 'code',
          direction: 'desc',
        },
      ];
    },
    isSuperAdmin() {
      return isAuthorised(authRoles.SuperAdmin);
    },
    // Overrides SharedListAccessControl.mixin: same role gate, plus reserved (400-499)
    // records may only be deleted by TmcSupport.
    canDeleteFromSharedList() {
      if (!this.canEditSharedList) return false;
      if (this.isSuperAdmin) return true;
      const statusCode = this.editedItem.statusCode || 0;
      if ((statusCode >= reservedStatusCode.min && statusCode <= reservedStatusCode.max)
       && this.editedItem._id) return false;
      return true;
    },
    variableToChangeRowBgColor() {
      return getStatusCodeRowColorMap();
    },
  },
  methods: {
    ...mapMutations('base/crud', ['setCriteria']),
    ...mapMutations('shared/taxStatusCodesShared', [
      'setSelectedItems',
    ]),
    ...mapActions(['reAuth', 'flashError']),
    ...mapActions('shared/taxStatusCodesShared', [
      'loadTaxStatusCodesSharedRecords',
      'upserttaxStatusCodes',
      'deleteTaxStatusCode',
    ]),
    emitSelectedItem(id) {
      this.selectedItems = id;
    },
    dataMapper: applyStatusCodeHighlight,
    async upsertRecords(item) {
      const { baseCrudKey } = this;
      const updatedItem = { ...item };
      delete updatedItem.isTrusted;
      // dataMapper (applyStatusCodeHighlight) flags a row with its own highlightColor as a
      // boolean key for CRUDList's row-coloring - strip it back off before it hits GraphQL,
      // whose input type has no such field.
      getHighlightColorForStatusCode.forEach(({ value }) => {
        if (value) delete updatedItem[value];
      });
      if (updatedItem.statusCode) {
        await this.upserttaxStatusCodes({ item: updatedItem, baseCrudKey });
      }
    },
    deleteFromDiaglog(item) {
      if (window.confirm('Are you sure you want to delete this item?')) {
        const { baseCrudKey } = this;
        this.deleteTaxStatusCode({ item, baseCrudKey });
      }
    },
    getTaxStatusCodesSharedRecords() {
      const { baseCrudKey } = this;
      manageGridLoading(this, true, 'Searching...');
      const criteria = { ...this.criteriaState, sortBy: [{ column: 'statusCode', direction: 'asc' }] };
      this.loadTaxStatusCodesSharedRecords(
        { criteria, baseCrudKey },
      ).finally(() => {
        manageGridLoading(this, false);
      });
    },
    async printStatusCodesReport() {
      const { jwt } = await this.reAuth();
      const params = [
        `token=${jwt}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/shared/status-report${queryString}`;
      window.open(url, '_blank');
    },
  },
};
</script>
